import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { ChevronRight, More } from '@wix/wix-ui-icons-common';

import PopoverMenu from '../PopoverMenu';
import Button from '../Button';
import IconButton from '../IconButton';
import Tooltip from '../Tooltip';
import { DATA_HOOKS } from './constants';
import HoverSlot from './HoverSlot';
import { st, classes } from './TableActionCell.st.css';
import { TooltipCommonProps } from '../common/PropTypes/TooltipCommon';
import { placements } from '../Popover';
import deprecationLog from '../utils/deprecationLog';

const blurElementWithoutFocusVisible = element => {
  if (process.env.NODE_ENV !== 'test' && !element.matches(':focus-visible')) {
    document.activeElement.blur();
  }
};

const renderMainActionButton = (
  {
    dataHook,
    text,
    skin,
    onClick,
    disabled,
    prefixIcon,
    suffixIcon,
    as,
    priority,
    visibility,
    disabledDescription,
    tooltipProps,
    ...rest
  },
  size,
) => {
  const visibilityPriorityMap = {
    always: 'secondary',
    onHover: 'primary',
  };
  const isDisabledWithTooltip = disabled && !!disabledDescription;

  function renderButton() {
    return (
      <Button
        key={text}
        as={as}
        dataHook={dataHook || DATA_HOOKS.primaryAction}
        className={st(classes.action, { size })}
        disabled={disabled}
        skin={skin}
        priority={visibilityPriorityMap[visibility] ?? priority}
        size={size}
        prefixIcon={prefixIcon}
        suffixIcon={suffixIcon}
        onClick={event => {
          onClick();
          blurElementWithoutFocusVisible(event.target);

          // Making sure we don't also trigger onRowClick.
          event.stopPropagation();
        }}
        {...rest}
      >
        {text}
      </Button>
    );
  }

  if (isDisabledWithTooltip) {
    return (
      <Tooltip
        dataHook={DATA_HOOKS.primaryActionTooltip}
        disabled={false}
        content={disabledDescription}
        {...tooltipProps}
      >
        {renderButton()}
      </Tooltip>
    );
  }

  return renderButton();
};

const renderVisibleActions = (actions, size) =>
  actions.map(
    (
      {
        text,
        icon,
        onClick,
        dataHook,
        disabled,
        disabledDescription,
        tooltipProps,
        skin,
      },
      index,
    ) => (
      <Tooltip
        key={index}
        dataHook={dataHook || DATA_HOOKS.visibleActionTooltip}
        disabled={disabled && disabledDescription === ''}
        content={
          disabled && Boolean(disabledDescription) ? disabledDescription : text
        }
        {...tooltipProps}
      >
        <IconButton
          dataHook={DATA_HOOKS.visibleAction}
          priority="secondary"
          disabled={disabled}
          className={st(classes.action, { size })}
          onClick={event => {
            onClick();
            blurElementWithoutFocusVisible(event.target);

            event.stopPropagation();
          }}
          size={size}
          ariaLabel={text}
          skin={skin}
        >
          {icon}
        </IconButton>
      </Tooltip>
    ),
  );

const renderHiddenActions = (
  actions,
  popoverMenuProps,
  size,
  moreActionsTooltipText,
) => (
  <PopoverMenu
    dataHook={DATA_HOOKS.popoverMenu}
    appendTo="parent"
    placement="top"
    textSize="small"
    triggerElement={({ toggle, isOpen, ref }) => (
      <Tooltip
        content={moreActionsTooltipText}
        disabled={!moreActionsTooltipText}
      >
        {({ onFocus, onBlur }) => (
          <IconButton
            onBlur={onBlur}
            priority="secondary"
            className={st(classes.action, { size })}
            dataHook={DATA_HOOKS.triggerElement}
            size={size}
            onClick={toggle}
            ref={ref}
            ariaLabel={moreActionsTooltipText || undefined}
            aria-haspopup="menu"
            aria-expanded={isOpen}
            onFocus={(event, { focus }) => {
              onFocus(event, {
                blur: () => blurElementWithoutFocusVisible(event.target),
                focus,
              });
            }}
          >
            <More />
          </IconButton>
        )}
      </Tooltip>
    )}
    {...popoverMenuProps}
  >
    {actions.map(
      (
        {
          text,
          icon,
          onClick,
          disabled,
          disabledDescription,
          dataHook,
          divider,
          skin,
          subtitle,
        },
        index,
      ) =>
        !divider ? (
          <PopoverMenu.MenuItem
            skin={skin ?? 'dark'}
            key={index}
            dataHook={dataHook || DATA_HOOKS.popoverMenuItem}
            prefixIcon={icon}
            onClick={() => onClick()}
            text={text}
            disabled={disabled}
            disabledDescription={disabledDescription}
            subtitle={subtitle}
          />
        ) : (
          <PopoverMenu.Divider />
        ),
    )}
  </PopoverMenu>
);

const renderPlaceholder = size => (
  <IconButton
    dataHook={DATA_HOOKS.placeholder}
    size={size}
    priority="secondary"
    tabIndex={-1}
  >
    <ChevronRight />
  </IconButton>
);

const TableActionCell = ({
  onClick = () => {},
  dataHook,
  primaryAction = null,
  secondaryActions = [],
  numOfVisibleSecondaryActions = 0,
  alwaysShowSecondaryActions = false,
  alwaysShowPrimaryActions = false,
  moreActionsTooltipText,
  popoverMenuProps,
  size = 'medium',
}) => {
  useEffect(() => {
    if (primaryAction?.skin) {
      deprecationLog(
        '<TableActionCell/> - prop "primaryAction" with key "skin" is deprecated and will be removed in next major release. It has been replaced by the visibility property, which switches skin properties automatically based on visibility options.',
      );
    }
  }, [primaryAction?.skin]);

  const visibleActions = secondaryActions.slice(
    0,
    numOfVisibleSecondaryActions,
  );
  const hiddenActions = secondaryActions.slice(numOfVisibleSecondaryActions);

  const isPrimaryActionArray = Array.isArray(primaryAction);
  const primaryActionDisplay = isPrimaryActionArray
    ? alwaysShowPrimaryActions
      ? 'always'
      : 'onHover'
    : (primaryAction?.visibility ?? 'onHover');

  const shouldShowPlaceholder = isPrimaryActionArray
    ? !alwaysShowPrimaryActions
    : primaryAction?.visibility !== 'always';

  return (
    <span
      data-hook={dataHook}
      className={classes.root}
      data-more-actions-tooltip-text={moreActionsTooltipText}
    >
      {primaryAction && (
        <HoverSlot
          data-hook={DATA_HOOKS.primaryActionWrapper}
          display={primaryActionDisplay}
        >
          {isPrimaryActionArray
            ? primaryAction.map(action => renderMainActionButton(action, size))
            : renderMainActionButton(primaryAction, size)}
        </HoverSlot>
      )}

      {visibleActions.length > 0 && (
        <HoverSlot
          data-hook={DATA_HOOKS.visibleActionsWrapper}
          display={alwaysShowSecondaryActions ? 'always' : 'onHover'}
        >
          {renderVisibleActions(visibleActions, size)}
        </HoverSlot>
      )}

      {hiddenActions.length > 0 && (
        <div onClick={e => e.stopPropagation()}>
          <HoverSlot display="always">
            {renderHiddenActions(
              hiddenActions,
              popoverMenuProps,
              size,
              moreActionsTooltipText,
            )}
          </HoverSlot>
        </div>
      )}

      {primaryAction &&
        shouldShowPlaceholder &&
        (!secondaryActions.length ||
          secondaryActions.length === numOfVisibleSecondaryActions) && (
          <HoverSlot display="notOnHover" className={classes.placeholderIcon}>
            {renderPlaceholder(size)}
          </HoverSlot>
        )}
    </span>
  );
};

TableActionCell.displayName = 'TableActionCell';

TableActionCell.propTypes = {
  /** Applies a data-hook HTML attribute to be used in the tests. */
  dataHook: PropTypes.string,

  /**
     Controls the behaviour and appearance of a primary action:
   - `text` specifies action label
   - `skin` <b>deprecated</b> property that was previously used to control the visual appearance of a button. It has been replaced by the visibility property, which switches skin properties automatically based on visibility options.
   - `onClick` defines a callback function which is called on button click
   - `disabled` restrict user interactions
   - `prefixIcon` add an icon in front of a button label
   - `suffixIcon` add an icon after button label
   - `as` renders button as another component or a DOM tag
   - `dataHook` applies a data-hook HTML attribute to be used in the tests
   - `disabledDescription` indicates an explanatory text string to display in tooltip when action is visible and disabled
   - `tooltipProps` controls the tooltip appearance
   - `visibility` specifies whether the primary action button is visible. When the value is set to "onHover", the button is only visible when the mouse hovers over it, and it appears as a primary button. When the value is set to "always", the button is always visible and appears as a secondary button.
   - `priority` specifies the button priority (only for array format)
   */
  primaryAction: PropTypes.oneOfType([
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      skin: PropTypes.oneOf(['standard', 'inverted', 'premium']),
      onClick: PropTypes.func.isRequired,
      disabled: PropTypes.bool,
      prefixIcon: PropTypes.element,
      suffixIcon: PropTypes.element,
      as: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.object,
        PropTypes.string,
      ]),
      dataHook: PropTypes.string,
      disabledDescription: PropTypes.string,
      tooltipProps: PropTypes.shape(TooltipCommonProps),
      visibility: PropTypes.oneOf(['onHover', 'always']),
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        skin: PropTypes.oneOf(['standard', 'inverted', 'premium']),
        onClick: PropTypes.func.isRequired,
        disabled: PropTypes.bool,
        prefixIcon: PropTypes.element,
        suffixIcon: PropTypes.element,
        as: PropTypes.oneOfType([
          PropTypes.func,
          PropTypes.object,
          PropTypes.string,
        ]),
        dataHook: PropTypes.string,
        disabledDescription: PropTypes.string,
        tooltipProps: PropTypes.shape(TooltipCommonProps),
        priority: PropTypes.oneOf(['primary', 'secondary']),
      }),
    ),
  ]),

  /**
   Controls the behaviour and appearance of secondary actions:

   Action object containing
   - `text` specifies action label to be shown in the tooltip or popover menu
   - `icon` specifies icon of an action
   - `onClick` defines a callback function which is called on button click
   - `subtitle` a subtitle for the action
   - `disabled` restrict user interactions
   - `dataHook` applies a data-hook HTML attribute to be used in the tests
   - `disabledDescription` indicates an explanatory text string to display in tooltip when action is visible and disabled
   - `tooltipProps` controls the tooltip appearance
   - `skin` controls the skin of the item

   OR Divider object containing
   - `divider` displays a divider between the action items in popover menu
   */
  secondaryActions: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        icon: PropTypes.node.isRequired,
        onClick: PropTypes.func.isRequired,
        subtitle: PropTypes.string,
        disabled: PropTypes.bool,
        dataHook: PropTypes.string,
        disabledDescription: PropTypes.string,
        tooltipProps: PropTypes.shape(TooltipCommonProps),
        skin: PropTypes.oneOf(['dark', 'destructive', 'premium']),
      }),
      PropTypes.shape({
        divider: PropTypes.bool.isRequired,
      }),
    ]),
  ),

  /** Specifies the number of secondary actions to display outside of the popover menu */
  numOfVisibleSecondaryActions: PropTypes.number,

  /** Specifies whether secondary actions should be visible all the time. By default secondary actions are visible on row hover only. */
  alwaysShowSecondaryActions: PropTypes.bool,

  /** Specifies whether primary actions should be visible all the time. By default primary actions are visible on row hover only. */
  alwaysShowPrimaryActions: PropTypes.bool,

  /** Sets the tooltip text of more actions icon button */
  moreActionsTooltipText: PropTypes.string,

  /** Controls the appearance of secondary actions menu with all common popover menu props */
  popoverMenuProps: PropTypes.shape({
    /** The maximum width applied to the list */
    maxWidth: PropTypes.number,

    /** The minimum width applied to the list */
    minWidth: PropTypes.number,

    /** The maximum height value applied to the list */
    maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

    /** Popover content z-index */
    zIndex: PropTypes.number,

    /** Moves popover content relative to the parent by x or y */
    moveBy: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),

    /** The Popover's placement:
     *  * auto-start
     *  * auto
     *  * auto-end
     *  * top-start
     *  * top
     *  * top-end
     *  * right-start
     *  * right
     *  * right-end
     *  * bottom-end
     *  * bottom
     *  * bottom-start
     *  * left-end
     *  * left
     *  * left-start
     */
    placement: PropTypes.oneOf(placements),

    /** Changing text size */
    textSize: PropTypes.oneOf(['small', 'medium']),

    /** Enables text ellipsis on tight containers */
    ellipsis: PropTypes.bool,

    /** The Popover's appendTo */
    appendTo: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

    /**
     * Whether to enable the flip behaviour. This behaviour is used to flip the `<Popover/>`'s placement
     * when it starts to overlap the target element (`<Popover.Element/>`).
     */
    flip: PropTypes.bool,
    /**
     * Whether to enable the fixed behaviour. This behaviour is used to keep the `<Popover/>` at it's
     * original placement even when it's being positioned outside the boundary.
     */
    fixed: PropTypes.bool,

    /** Whether to show the Popover's arrow */
    showArrow: PropTypes.bool,
  }),

  /** Controls the size of action buttons */
  size: PropTypes.oneOf(['tiny', 'small', 'medium']),
};

export default TableActionCell;
